"use client"

import type React from "react"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useRouter } from "next/navigation"
import type { Area, GroupedAreas } from "@/types/area"
import type { School } from "@/types/school"

export const useSchoolFinder = (
  initialAreas: string[] = [],
  initialSchools: School[] = [],
  initialParams: any = {},
) => {
  const router = useRouter()

  const [selectedAreas, setSelectedAreas] = useState<string[]>(initialAreas)
  const [areas, setAreas] = useState<Area[]>([])
  const [groupedAreas, setGroupedAreas] = useState<GroupedAreas>({})
  const [schoolType, setSchoolType] = useState<string>(initialParams.type || "")
  const [schoolLevel, setSchoolLevel] = useState<string[]>(initialParams.level?.split(",") || [])
  const [academicPerformance, setAcademicPerformance] = useState<string>(initialParams.performance || "")
  const [schools, setSchools] = useState<School[]>(initialSchools)
  const [error, setError] = useState<string | null>(null)
  const [inputErrors, setInputErrors] = useState<{ [key: string]: string }>({})
  const [noResultsMessage, setNoResultsMessage] = useState<string | null>(null)
  const [area, setArea] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)

  const fetchAreas = useCallback(async () => {
    try {
      const response = await fetch("/api/areas")
      if (!response.ok) {
        throw new Error("Failed to fetch areas")
      }
      const fetchedAreas: Area[] = await response.json()
      setAreas(fetchedAreas)
    } catch (error) {
      console.error("Error fetching areas:", error)
      setError("Failed to fetch areas. Please try again later.")
    }
  }, [])

  useEffect(() => {
    fetchAreas()
  }, [fetchAreas])

  const memoizedGroupedAreas = useMemo(() => {
    if (selectedAreas.length > 0 && areas.length > 0) {
      const filtered = areas.filter((a) =>
        selectedAreas.some(
          (area) =>
            typeof area === "string" &&
            (a.name.toLowerCase().includes(area.toLowerCase()) || a.kommun.toLowerCase().includes(area.toLowerCase())),
        ),
      )
      const grouped = filtered.reduce((acc, curr) => {
        if (!acc[curr.kommun]) {
          acc[curr.kommun] = []
        }
        const existingArea = acc[curr.kommun].find((a) => a.name === curr.name)
        if (!existingArea) {
          acc[curr.kommun].push(curr)
        }
        return acc
      }, {} as GroupedAreas)

      Object.keys(grouped).forEach((kommun) => {
        grouped[kommun].sort((a, b) => a.name.localeCompare(b.name, "sv"))
      })

      return grouped
    }
    return {}
  }, [selectedAreas, areas])

  useEffect(() => {
    setGroupedAreas(memoizedGroupedAreas)
  }, [memoizedGroupedAreas])

  const handleAreaChange = useCallback(
    (value: string) => {
      setArea(value)
      if (value.length > 2) {
        const filtered = areas.filter(
          (a) =>
            !selectedAreas.includes(a.name) &&
            (a.name.toLowerCase().includes(value.toLowerCase()) ||
              a.kommun.toLowerCase().includes(value.toLowerCase())),
        )
        const grouped = filtered.reduce((acc, curr) => {
          if (!acc[curr.kommun]) {
            acc[curr.kommun] = []
          }
          const existingArea = acc[curr.kommun].find((a) => a.name === curr.name)
          if (!existingArea) {
            acc[curr.kommun].push(curr)
          }
          return acc
        }, {} as GroupedAreas)

        Object.keys(grouped).forEach((kommun) => {
          grouped[kommun].sort((a, b) => a.name.localeCompare(b.name, "sv"))
        })

        setGroupedAreas(grouped)
      } else {
        setGroupedAreas({})
      }
    },
    [areas, selectedAreas],
  )

  const handleAddArea = useCallback(
    (newArea: Area) => {
      if (newArea && newArea.name && !selectedAreas.includes(newArea.name)) {
        setSelectedAreas((prev) => [...prev, newArea.name])
      }
      setArea("")
      setGroupedAreas({})
    },
    [selectedAreas],
  )

  const handleAddMultipleAreas = useCallback((areas: Area[]) => {
    setSelectedAreas((prev) => {
      const newAreas = areas.map((area) => area.name)
      return [...new Set([...prev, ...newAreas])]
    })
    setArea("")
    setGroupedAreas({})
  }, [])

  const handleSubmit = async (e: React.FormEvent): Promise<{ url: string } | void> => {
    e.preventDefault()
    setError(null)
    setInputErrors({})
    setSchools([])
    setIsLoading(true)

    const errors: { [key: string]: string } = {}
    if (selectedAreas.length === 0) {
      errors.area = "Vänligen välj minst ett område"
    }

    if (Object.keys(errors).length > 0) {
      setInputErrors(errors)
      setIsLoading(false)
      return
    }

    try {
      const params = new URLSearchParams()
      selectedAreas.forEach((area) => params.append("areas", area))
      if (schoolType) params.set("type", schoolType)
      if (schoolLevel.length > 0) params.set("level", schoolLevel.join(","))
      if (academicPerformance) params.set("performance", academicPerformance)

      const response = await fetch(`/api/schools?${params.toString()}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch schools")
      }

      if (Array.isArray(data) && data.length === 0) {
        setNoResultsMessage("0 skolor matchade dina kriterier")
        setSchools([])
      } else if (Array.isArray(data)) {
        setNoResultsMessage(null)
        setSchools(data)
      } else {
        throw new Error("Unexpected data structure received from server")
      }

      return { url: `?${params.toString()}` }
    } catch (error) {
      console.error("Error fetching schools:", error)
      setError("Det uppstod ett fel vid hämtning av skolor. Försök igen senare.")
    } finally {
      setIsLoading(false)
    }
  }

  const resetAllCriteria = useCallback(() => {
    setSelectedAreas([])
    setSchoolType("")
    setSchoolLevel([])
    setAcademicPerformance("")
    setArea("")
    setGroupedAreas({})
    setSchools([])
    setNoResultsMessage(null)
    router.push("/skola", undefined, { shallow: true })
  }, [router])

  return {
    selectedAreas,
    setSelectedAreas,
    areas,
    groupedAreas,
    setGroupedAreas,
    schoolType,
    setSchoolType,
    schoolLevel,
    setSchoolLevel,
    academicPerformance,
    setAcademicPerformance,
    schools,
    setSchools,
    error,
    inputErrors,
    handleSubmit,
    noResultsMessage,
    setNoResultsMessage,
    area,
    handleAreaChange,
    handleAddArea,
    handleAddMultipleAreas,
    isLoading,
    setIsLoading,
    resetAllCriteria,
  }
}

